import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import {
  ArrowLeft,
  Printer,
  Loader2,
  Check,
  X,
  ClipboardList,
  FileText,
  Images,
} from "lucide-react";
import {
  VEHICLE_READINESS,
  COMMUNICATION_TECH,
  PATIENT_CABINET,
  FIX_ASSETS_COL1,
  FIX_ASSETS_COL2,
  DEFIBRILLATOR,
  ALS_RIGHT_POUCH,
  ALS_LEFT_POUCH,
  ALS_MAIN_COMPARTMENT,
  ALS_INTUBATION,
  UNDER_BENCH,
  COMP_A,
  COMP_B,
  COMP_C,
  COMP_D,
  COMP_E,
  FRONT_POUCH,
  REAR_POUCH,
} from "@ambulance/shared";
import { NewChecklistDetail } from "../components/NewChecklistDetail";

type Items = ReadonlyArray<
  readonly [string, string] | readonly [string, string, string]
>;

function YN({ on }: { on: boolean | undefined }) {
  // Yes column: render filled green with check if on; otherwise empty
  return (
    <td className={`mark ${on ? "mark-yes" : ""}`}>{on ? "✓" : ""}</td>
  );
}
function YNo({ on }: { on: boolean | undefined }) {
  // The "No" column: only render filled red with × if explicitly NOT available
  return (
    <td className={`mark ${on === false ? "" : ""}`}>{""}</td>
  );
}

/** Yes/No row pair — value is true (yes) / false (no/blank). */
function YesNoRow({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <tr>
      <td className="item">{label}</td>
      <td className={`mark ${value ? "mark-yes" : ""}`}>{value ? "✓" : ""}</td>
      <td className={`mark ${value === false ? "mark-no" : ""}`}>
        {value === false ? "✗" : ""}
      </td>
    </tr>
  );
}

/** Generic Yes/No section table */
function YesNoSection({
  title,
  items,
  values,
  rows = items.length,
}: {
  title: string;
  items: Items;
  values: Record<string, boolean>;
  rows?: number;
}) {
  const filler = Math.max(0, rows - items.length);
  return (
    <table>
      <thead>
        <tr>
          <th className="sec-green" colSpan={1}>
            {title}
          </th>
          <th className="yes-h">Yes</th>
          <th className="no-h">No</th>
        </tr>
      </thead>
      <tbody>
        {items.map((tuple) => {
          const [k, label] = tuple;
          return <YesNoRow key={k} label={label} value={values[k]} />;
        })}
        {Array.from({ length: filler }).map((_, i) => (
          <tr key={`f${i}`} className="row-h">
            <td>&nbsp;</td>
            <td></td>
            <td></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** QTY/# section (compartments / pouches) */
function QtySection({
  title,
  items,
  values,
  rows = items.length,
  green = true,
}: {
  title: string;
  items: Items;
  values: Record<string, boolean>;
  rows?: number;
  green?: boolean;
}) {
  const filler = Math.max(0, rows - items.length);
  const headerCls = green ? "sec-green" : "sec-gray";
  return (
    <table>
      <thead>
        <tr>
          <th className={headerCls}>{title}</th>
          <th className="qty-h">QTY</th>
          <th className="num-h">#</th>
        </tr>
      </thead>
      <tbody>
        {items.map((tuple) => {
          const [k, label, qty] = tuple as readonly [string, string, string?];
          return (
            <tr key={k}>
              <td className="item">{label}</td>
              <td className="mark">{qty ?? ""}</td>
              <td className={`mark ${values[k] ? "mark-yes" : ""}`}>
                {values[k] ? "✓" : ""}
              </td>
            </tr>
          );
        })}
        {Array.from({ length: filler }).map((_, i) => (
          <tr key={`f${i}`} className="row-h">
            <td>&nbsp;</td>
            <td></td>
            <td></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ChecklistDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const api = useApi();
  const q = useQuery({ queryKey: ["checklist", id, api.token], queryFn: () => api.get<Record<string, unknown>>(`/api/v1/checklists/${id}`), enabled: Boolean(api.token) && Boolean(id) });
  const dataRaw = q.isPending ? undefined : q.isError ? null : q.data;
  // The legacy replica below predates the now-optional schema fields; old
  // records always carry them, so treat the document loosely here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = dataRaw as any;
  const [tab, setTab] = useState<"checklist" | "report" | "images">("checklist");

  if (data === undefined) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading checklist…
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-700 mb-4">Checklist not found.</p>
        <button
          onClick={() => navigate({ to: "/Checklists-Overview" })}
          className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300"
        >
          Back to checklists
        </button>
      </div>
    );
  }

  // Records submitted with the revised form render the new read-only view;
  // older records keep the legacy replica below.
  if (data.vehicleMechanical) {
    return (
      <NewChecklistDetail
        data={data}
        onBack={() => navigate({ to: "/Checklists-Overview" })}
      />
    );
  }

  // Date split into D/M/Y boxes like the PDF
  const [yyyy, mm, dd] = (data.date || "").split("-");

  return (
    <div className="force-print-colors">
      {/* Action bar + tabs (hidden on print) */}
      <div className="no-print max-w-5xl mx-auto mb-4 px-2">
        <div className="flex items-center justify-between gap-2 mb-3">
          <button
            onClick={() => navigate({ to: "/Checklists-Overview" })}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {tab === "report" && (
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-[var(--hmg-red)] text-white hover:bg-[var(--hmg-red-dark)] text-sm font-semibold"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print / Save PDF</span>
              <span className="sm:hidden">Print</span>
            </button>
          )}
        </div>
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {(
            [
              ["checklist", "Checklist", ClipboardList],
              ["report", "Report", FileText],
              ["images", "Images", Images],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition ${
                tab === key
                  ? "border-[var(--hmg-red)] text-[var(--hmg-red)]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* CHECKLIST TAB — readable view */}
      {tab === "checklist" && (
        <div className="no-print max-w-5xl mx-auto px-2 pb-8">
          <ReadableView data={data} />
        </div>
      )}

      {/* IMAGES TAB */}
      {tab === "images" && (
        <div className="no-print max-w-5xl mx-auto px-2 pb-8">
          <ImagesView images={data.images} />
        </div>
      )}

      {/* REPORT TAB — always in the DOM so printing works from any tab */}
      <div
        className={`pdf-form-wrap ${tab === "report" ? "" : "hidden"} print:block`}
      >
      <div className="pdf-form">
        {/* TOP HEADER */}
        <table>
          <tbody>
            <tr>
              <td colSpan={6} className="top-title" style={{ width: "82%" }}>
                <div style={{ fontSize: "9pt" }}>HMG-Emergency Medical Services</div>
                <div style={{ fontSize: "11pt" }}>
                  AMBULANCE DAILY CHECKLIST FORM
                </div>
              </td>
              <td
                colSpan={2}
                rowSpan={3}
                style={{
                  width: "18%",
                  textAlign: "center",
                  background: "#fff",
                  padding: 4,
                }}
              >
                <img
                  src="/hmg-logo.png"
                  alt="HMG"
                  style={{ maxHeight: 50, maxWidth: "100%" }}
                />
              </td>
            </tr>
            <tr>
              <td className="field-label" style={{ width: "10%" }}>
                Ambulance No:
              </td>
              <td style={{ width: "20%" }}>{data.ambulanceNo || ""}</td>
              <td className="field-label" style={{ width: "8%" }}>
                Date:
              </td>
              <td style={{ width: "20%" }}>
                <span style={{ display: "inline-block", minWidth: 24 }}>
                  {dd || ""}
                </span>{" "}
                /{" "}
                <span style={{ display: "inline-block", minWidth: 24 }}>
                  {mm || ""}
                </span>{" "}
                /{" "}
                <span style={{ display: "inline-block", minWidth: 36 }}>
                  {yyyy || ""}
                </span>
              </td>
              <td className="field-label" style={{ width: "6%" }}>
                Shift
              </td>
              <td style={{ width: "18%" }}>
                <span
                  className={data.shift === "D" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  D
                </span>
                {"   "}
                <span
                  className={data.shift === "N" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  N
                </span>
                <span style={{ marginLeft: 12 }} className="field-label">
                  Type
                </span>{" "}
                <span
                  className={data.type === "I" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  I
                </span>
                {"  "}
                <span
                  className={data.type === "II" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  II
                </span>
              </td>
            </tr>
            <tr>
              <td className="field-label">Mileage</td>
              <td>
                {data.mileage || ""}{" "}
                <span style={{ float: "right" }}>KM</span>
              </td>
              <td className="field-label">Hospital:</td>
              <td>{data.hospitalName}</td>
              <td className="field-label">Level</td>
              <td>
                <span
                  className={data.level === "BLS" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  BLS
                </span>
                {"  "}
                <span
                  className={data.level === "ALS" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  ALS
                </span>
                {"  "}
                <span
                  className={data.level === "CC" ? "mark-yes" : ""}
                  style={{ padding: "0 4px", fontWeight: "bold" }}
                >
                  CC
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan={8} className="red-band">
                I External Body & Mechanical
              </td>
            </tr>
          </tbody>
        </table>

        {/* VEHICLE READINESS + ambulance views (4 columns merged for views) */}
        <table>
          <thead>
            <tr>
              <th className="sec-green" style={{ width: "20%" }}>
                Vehicle Readiness
              </th>
              <th className="yes-h">Yes</th>
              <th className="no-h">No</th>
              <th className="sec-green" style={{ width: "16%" }}>
                Front Side
              </th>
              <th className="sec-green" style={{ width: "16%" }}>
                Rear Side
              </th>
              <th className="sec-green" style={{ width: "16%" }}>
                Right Side
              </th>
              <th className="sec-green" style={{ width: "16%" }}>
                Left Side
              </th>
            </tr>
          </thead>
          <tbody>
            {VEHICLE_READINESS.map(([k, label], idx) => (
              <tr key={k}>
                <td className="item">{label}</td>
                <td
                  className={`mark ${data.vehicleReadiness[k as keyof typeof data.vehicleReadiness] ? "mark-yes" : ""}`}
                >
                  {data.vehicleReadiness[k as keyof typeof data.vehicleReadiness]
                    ? "✓"
                    : ""}
                </td>
                <td
                  className={`mark ${data.vehicleReadiness[k as keyof typeof data.vehicleReadiness] === false ? "mark-no" : ""}`}
                >
                  {data.vehicleReadiness[k as keyof typeof data.vehicleReadiness] === false
                    ? "✗"
                    : ""}
                </td>
                {idx === 0 && (
                  <>
                    <td rowSpan={VEHICLE_READINESS.length} style={{ verticalAlign: "top", padding: 4 }}>
                      <div style={{ minHeight: 60, fontSize: "7pt", color: "#666", textAlign: "center", paddingTop: 4 }}>
                        (front view)
                      </div>
                    </td>
                    <td rowSpan={VEHICLE_READINESS.length} style={{ verticalAlign: "top", padding: 4 }}>
                      <div style={{ minHeight: 60, fontSize: "7pt", color: "#666", textAlign: "center", paddingTop: 4 }}>
                        (rear view)
                      </div>
                    </td>
                    <td rowSpan={VEHICLE_READINESS.length} style={{ verticalAlign: "top", padding: 4 }}>
                      <div style={{ minHeight: 60, fontSize: "7pt", color: "#666", textAlign: "center", paddingTop: 4 }}>
                        (right view)
                      </div>
                    </td>
                    <td rowSpan={VEHICLE_READINESS.length} style={{ verticalAlign: "top", padding: 4 }}>
                      <div style={{ minHeight: 60, fontSize: "7pt", color: "#666", textAlign: "center", paddingTop: 4 }}>
                        (left view)
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr>
              <td colSpan={3} className="field-label">
                Note: {data.bodyNote || ""}
              </td>
              <td colSpan={4}></td>
            </tr>
            <tr>
              <td colSpan={3}>
                <span className="field-label">Outgoing Medic Name: </span>
                {data.outgoingMedicName || ""}
              </td>
              <td colSpan={2}>
                <span className="field-label">Incoming Medic Name: </span>
                {data.incomingMedicName || ""}
              </td>
              <td colSpan={2}>
                <span className="field-label">Badge No: </span>
                {data.medicBadgeNo || ""}
              </td>
            </tr>
            <tr>
              <td colSpan={7} className="red-band">
                II Medical & Cabinet Readiness
              </td>
            </tr>
          </tbody>
        </table>

        {/* Main 4-column grid mirroring PDF: Comm/Cabinet, Fix1, Fix2, Compartments */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 0,
          }}
        >
          {/* Column 1: Comm + Patient Cabinet + Medication Bag + Defibrillator */}
          <div>
            <YesNoSection
              title="Communication & Technology"
              items={COMMUNICATION_TECH}
              values={data.communicationTechnology}
            />
            <YesNoSection
              title="Patient Cabinet"
              items={PATIENT_CABINET}
              values={data.patientCabinet}
            />
            <table>
              <thead>
                <tr>
                  <th className="sec-green">Medication Bag</th>
                  <th className="yes-h">Yes</th>
                  <th className="no-h">No</th>
                </tr>
              </thead>
              <tbody>
                <YesNoRow
                  label="Bag is Available"
                  value={data.medicationBag.bagAvailable}
                />
                <YesNoRow
                  label="Bag is Sealed"
                  value={data.medicationBag.bagSealed}
                />
                <tr>
                  <td className="item field-label">Seal No.</td>
                  <td colSpan={2}>{data.medicationBag.sealNo || ""}</td>
                </tr>
              </tbody>
            </table>
            <YesNoSection
              title="Defibrillator"
              items={DEFIBRILLATOR}
              values={data.defibrillator}
            />
          </div>

          {/* Column 2: Fix Assets 1 + ALS Right + ALS Left + Main Compartment */}
          <div>
            <YesNoSection
              title="Fix Assets"
              items={FIX_ASSETS_COL1}
              values={data.fixAssetsCol1}
            />
            <table>
              <thead>
                <tr>
                  <th colSpan={3} className="red-band">
                    III ALS Bag
                  </th>
                </tr>
              </thead>
            </table>
            <QtySection
              title="Right Side Pouch"
              items={ALS_RIGHT_POUCH}
              values={data.alsBag.rightSidePouch as Record<string, boolean>}
            />
            <QtySection
              title="Left Side Pouch"
              items={ALS_LEFT_POUCH}
              values={data.alsBag.leftSidePouch as Record<string, boolean>}
            />
            <QtySection
              title="Intubation Kit ( A&P )"
              items={ALS_INTUBATION}
              values={data.alsBag.intubationKit as Record<string, boolean>}
            />
          </div>

          {/* Column 3: Fix Assets 2 + Main Compartment + Under Bench */}
          <div>
            <YesNoSection
              title="Fix Assets"
              items={FIX_ASSETS_COL2}
              values={data.fixAssetsCol2}
            />
            <QtySection
              title="Main Compartment"
              items={ALS_MAIN_COMPARTMENT}
              values={data.alsBag.mainCompartment as Record<string, boolean>}
            />
            <YesNoSection
              title="Under Bench"
              items={UNDER_BENCH}
              values={data.underBench}
            />
            <QtySection
              title="Front Pouch"
              items={FRONT_POUCH}
              values={data.frontPouch}
            />
            <QtySection
              title="Rear Pouch"
              items={REAR_POUCH}
              values={data.rearPouch}
            />
          </div>

          {/* Column 4: Compartments A/B/C/D/E */}
          <div>
            <QtySection
              title="Compartment A"
              items={COMP_A}
              values={data.compartmentA}
              green={false}
            />
            <QtySection
              title="Compartment B"
              items={COMP_B}
              values={data.compartmentB}
              green={false}
            />
            <QtySection
              title="Compartment C"
              items={COMP_C}
              values={data.compartmentC}
              green={false}
            />
            <QtySection
              title="Compartment D"
              items={COMP_D}
              values={data.compartmentD}
              green={false}
            />
            <QtySection
              title="Compartment E"
              items={COMP_E}
              values={data.compartmentE}
              green={false}
            />
          </div>
        </div>

        {/* FOOTER STRIP: Team leaders + signoff */}
        <table>
          <tbody>
            <tr>
              <td className="red-band" style={{ width: "60%" }}>
                Please Complete checklist each shift
              </td>
              <td className="sec-green" style={{ width: "20%" }}>
                Outgoing Team Leader
              </td>
              <td className="sec-green" style={{ width: "20%" }}>
                Badge No
              </td>
            </tr>
            <tr>
              <td rowSpan={2} style={{ textAlign: "center", fontWeight: "bold" }}>
                Thank you for Checking, We Wish You Quiet and Safe Shift
              </td>
              <td>{data.outgoingTeamLeader || ""}</td>
              <td>{data.outgoingTeamLeaderBadge || ""}</td>
            </tr>
            <tr>
              <td className="sec-green">Ongoing Team Leader</td>
              <td className="sec-green">Badge No</td>
            </tr>
            <tr>
              <td style={{ fontSize: "7pt" }}>
                * Please make sure each compartment sealed if compartment is
                not sealed, refill and seal it / Mark (✓) Means{" "}
                <b>Available and NOT Expired</b>, Mark (✗) Means{" "}
                <b>NOT Available</b>
              </td>
              <td>{data.ongoingTeamLeader || ""}</td>
              <td>
                {data.ongoingTeamLeaderBadge || ""}{" "}
                <span className="red-text" style={{ float: "right" }}>
                  HMG/QID/20428-E1
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function ReadableRow({
  label,
  on,
}: {
  label: string;
  on: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
      <span className="text-slate-700">{label}</span>
      <span
        className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${
          on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
        }`}
      >
        {on ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {on ? "Yes" : "No"}
      </span>
    </div>
  );
}

function ReadableSection({
  title,
  items,
  values,
}: {
  title: string;
  items: Items;
  values: Record<string, boolean>;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden self-start">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-800">
        {title}
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((t) => (
          <ReadableRow key={t[0]} label={t[1]} on={values?.[t[0]]} />
        ))}
      </div>
    </div>
  );
}

function ReadableView({ data }: { data: any }) {
  const info: [string, string][] = [
    ["Hospital", data.hospitalName ?? "—"],
    ["Ambulance No", data.ambulanceNo ?? "—"],
    ["Date", data.date ?? "—"],
    ["Shift", data.shift === "D" ? "Day" : data.shift === "N" ? "Night" : "—"],
    ["Type", data.type ?? "—"],
    ["Level", data.level ?? "—"],
    ["Mileage (KM)", data.mileage || "—"],
    ["Outgoing Medic", data.outgoingMedicName || "—"],
    ["Incoming Medic", data.incomingMedicName || "—"],
    ["Medic Badge No", data.medicBadgeNo || "—"],
  ];
  const sections: { title: string; items: Items; values: any }[] = [
    { title: "Vehicle Readiness", items: VEHICLE_READINESS, values: data.vehicleReadiness },
    { title: "Communication & Technology", items: COMMUNICATION_TECH, values: data.communicationTechnology },
    { title: "Patient Cabinet", items: PATIENT_CABINET, values: data.patientCabinet },
    { title: "Defibrillator", items: DEFIBRILLATOR, values: data.defibrillator },
    { title: "Fix Assets — Column 1", items: FIX_ASSETS_COL1, values: data.fixAssetsCol1 },
    { title: "Fix Assets — Column 2", items: FIX_ASSETS_COL2, values: data.fixAssetsCol2 },
    { title: "ALS Bag — Right Side Pouch", items: ALS_RIGHT_POUCH, values: data.alsBag.rightSidePouch },
    { title: "ALS Bag — Left Side Pouch", items: ALS_LEFT_POUCH, values: data.alsBag.leftSidePouch },
    { title: "ALS Bag — Main Compartment", items: ALS_MAIN_COMPARTMENT, values: data.alsBag.mainCompartment },
    { title: "ALS Bag — Intubation Kit", items: ALS_INTUBATION, values: data.alsBag.intubationKit },
    { title: "Under Bench", items: UNDER_BENCH, values: data.underBench },
    { title: "Compartment A", items: COMP_A, values: data.compartmentA },
    { title: "Compartment B", items: COMP_B, values: data.compartmentB },
    { title: "Compartment C", items: COMP_C, values: data.compartmentC },
    { title: "Compartment D", items: COMP_D, values: data.compartmentD },
    { title: "Compartment E", items: COMP_E, values: data.compartmentE },
    { title: "Front Pouch", items: FRONT_POUCH, values: data.frontPouch },
    { title: "Rear Pouch", items: REAR_POUCH, values: data.rearPouch },
  ];
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {info.map(([k, v]) => (
            <div key={k}>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                {k}
              </div>
              <div className="text-sm font-medium text-slate-800">{v}</div>
            </div>
          ))}
        </div>
        {data.bodyNote ? (
          <div className="mt-3 text-sm">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              Notes:{" "}
            </span>
            <span className="text-slate-700">{data.bodyNote}</span>
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-800">
          Medication Bag
        </div>
        <div className="divide-y divide-slate-100">
          <ReadableRow label="Bag is Available" on={data.medicationBag?.bagAvailable} />
          <ReadableRow label="Bag is Sealed" on={data.medicationBag?.bagSealed} />
          <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
            <span className="text-slate-700">Seal No</span>
            <span className="text-slate-800 font-medium">
              {data.medicationBag?.sealNo || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        {sections.map((s) => (
          <ReadableSection
            key={s.title}
            title={s.title}
            items={s.items}
            values={s.values}
          />
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-800 mb-3">
          Team Leader Signatures
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-400 block">
              Outgoing Team Leader
            </span>
            {data.outgoingTeamLeader || "—"}
            {data.outgoingTeamLeaderBadge
              ? ` (${data.outgoingTeamLeaderBadge})`
              : ""}
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-400 block">
              Ongoing Team Leader
            </span>
            {data.ongoingTeamLeader || "—"}
            {data.ongoingTeamLeaderBadge
              ? ` (${data.ongoingTeamLeaderBadge})`
              : ""}
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-400 block">
              Submitted by
            </span>
            {data.submittedByName || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImagesView({
  images,
}: {
  images: { url?: string | null; label: string }[];
}) {
  if (!images || images.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
        No photos were attached to this checklist.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {images.map((img, i) => (
        <div
          key={i}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        >
          {img.url ? (
            <a href={img.url} target="_blank" rel="noopener noreferrer">
              <img
                src={img.url}
                alt={img.label}
                className="w-full aspect-[4/3] object-contain bg-slate-50"
              />
            </a>
          ) : (
            <div className="w-full aspect-[4/3] bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
              No image
            </div>
          )}
          <div className="px-3 py-2 text-sm font-medium text-slate-700">
            {img.label}
          </div>
        </div>
      ))}
    </div>
  );
}
